/**
 * Verification-specific Error Boundary
 * Handles verification flow errors with context-aware recovery options
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AppContainer } from './AppContainer';
import { VerticalStack } from './VerticalStack';
import { ScreenTitle } from './ScreenTitle';
import { ScreenDescription } from './ScreenDescription';
import { PrimaryButton } from './PrimaryButton';
import { SecondaryButton } from './SecondaryButton';
import { IconDisplay } from './IconDisplay';
import { designTokens } from '../../constants/designTokens';
import { logger } from '../../utils/logger';
import { sessionStateManager } from '../../utils/SessionStateManager';
import { LanguageContext, tUnsafe } from '../../i18n';

const errorLogger = logger.clone({ labels: { component: 'VerificationErrorBoundary' }});

interface VerificationErrorBoundaryProps {
  children: ReactNode;
  onRetry?: () => void;
  screenType?: 'camera' | 'document' | 'websocket' | 'general';
}

interface VerificationErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

export class VerificationErrorBoundary extends Component<
  VerificationErrorBoundaryProps,
  VerificationErrorBoundaryState
> {
  private readonly MAX_RETRIES = 3;
  static contextType = LanguageContext;

  constructor(props: VerificationErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(
    error: Error
  ): Partial<VerificationErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    errorLogger.error('Verification error caught', error instanceof Error ? error : new Error(`${error}`));

    this.setState({
      errorInfo,
    });

    // Log to external error tracking if available
    if (window.onerror) {
      window.onerror(error.message, '', 0, 0, error);
    }
  }

  handleRetry = () => {
    const { retryCount } = this.state;

    if (retryCount < this.MAX_RETRIES) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: retryCount + 1,
      });

      if (this.props.onRetry) {
        this.props.onRetry();
      }
    } else {
      errorLogger.warn('Max retry attempts reached', {});
    }
  };

  handleStartOver = () => {
    // Clear session and reset error state to restart the app
    sessionStateManager.clearSession();
    
    // Reset error state which will re-render the app
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    });
    
    // If we have a custom retry handler, use it
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  getErrorMessage(): { title: string; description: string } {
    const { error, retryCount } = this.state;
    const { screenType } = this.props;

    // Camera-specific errors
    if (screenType === 'camera') {
      if (error?.message.includes('Permission')) {
        return {
          title: tUnsafe('verificationErrorBoundary.cameraDenied.title'),
          description: tUnsafe('verificationErrorBoundary.cameraDenied.description'),
        };
      }
      if (error?.message.includes('NotFound')) {
        return {
          title: tUnsafe('verificationErrorBoundary.noCamera.title'),
          description: tUnsafe('verificationErrorBoundary.noCamera.description'),
        };
      }
      return {
        title: tUnsafe('verificationErrorBoundary.cameraError.title'),
        description: tUnsafe('verificationErrorBoundary.cameraError.description'),
      };
    }

    // Document capture errors
    if (screenType === 'document') {
      return {
        title: tUnsafe('verificationErrorBoundary.documentFailed.title'),
        description: tUnsafe('verificationErrorBoundary.documentFailed.description'),
      };
    }

    // WebSocket connection errors
    if (screenType === 'websocket') {
      return {
        title: tUnsafe('verificationErrorBoundary.connectionLost.title'),
        description: tUnsafe('verificationErrorBoundary.connectionLost.description'),
      };
    }

    // Generic errors with retry count awareness
    if (retryCount >= this.MAX_RETRIES) {
      return {
        title: tUnsafe('verificationErrorBoundary.multipleErrors.title'),
        description: tUnsafe('verificationErrorBoundary.multipleErrors.description'),
      };
    }

    return {
      title: tUnsafe('verificationErrorBoundary.default.title'),
      description: tUnsafe('verificationErrorBoundary.default.description'),
    };
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const { title, description } = this.getErrorMessage();
      const { retryCount } = this.state;
      const canRetry = retryCount < this.MAX_RETRIES;

      return (
        <AppContainer maxWidth="content">
          <VerticalStack spacing="lg" align="center">
            <IconDisplay
              src="/icons/error.svg"
              alt={tUnsafe('verificationErrorBoundary.iconAlt')}
              size="xl"
            />

            <VerticalStack spacing="md" align="center">
              <ScreenTitle>{title}</ScreenTitle>
              <ScreenDescription>{description}</ScreenDescription>

              {retryCount > 0 && canRetry && (
                <ScreenDescription>
                  {tUnsafe('verificationErrorBoundary.retryAttempt', {
                    attempt: retryCount,
                    max: this.MAX_RETRIES,
                  })}
                </ScreenDescription>
              )}
            </VerticalStack>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && (
              <details
                style={{
                  width: '100%',
                  padding: designTokens.spacing.md,
                  backgroundColor: designTokens.colors.background.secondary,
                  borderRadius: designTokens.borderRadius.md,
                  fontSize: designTokens.typography.fontSize.sm,
                }}
              >
                <summary
                  style={{
                    cursor: 'pointer',
                    marginBottom: designTokens.spacing.sm,
                    fontWeight: designTokens.typography.fontWeight.medium,
                  }}
                >
                  {tUnsafe('verificationErrorBoundary.errorDetails')}
                </summary>
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    margin: 0,
                    fontFamily: 'monospace',
                  }}
                >
                  {this.state.error.toString()}
                  {'\n\n'}
                  {tUnsafe('verificationErrorBoundary.errorType')}{' '}
                  {this.props.screenType || tUnsafe('verificationErrorBoundary.errorTypeGeneral')}
                  {this.state.errorInfo && (
                    <>
                      {'\n\n'}
                      {tUnsafe('verificationErrorBoundary.componentStack')}
                      {'\n'}
                      {this.state.errorInfo.componentStack}
                    </>
                  )}
                </pre>
              </details>
            )}

            <VerticalStack spacing="sm" style={{ width: '100%' }}>
              {canRetry && (
                <PrimaryButton onClick={this.handleRetry} fullWidth>
                  {tUnsafe('common.tryAgain')}
                </PrimaryButton>
              )}
              <SecondaryButton onClick={this.handleStartOver} fullWidth>
                {tUnsafe('common.startOver')}
              </SecondaryButton>
            </VerticalStack>
          </VerticalStack>
        </AppContainer>
      );
    }

    return this.props.children;
  }
}
